
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { apiService } from '../service/api.service'
import { createUrl } from '@/utils/helpers'
import environment from '../../environment'
import StatCard from '../components/dashboard/StatCard.vue'
import BarList from '../components/dashboard/BarList.vue'
import LineChart from '../components/dashboard/LineChart.vue'

export default Vue.extend({
  name: 'Dashboard',
  components: { StatCard, BarList, LineChart },
  data: () => ({
    clearingCache: false,
    cacheSnack: { show: false, color: 'success', text: '' },
    filterMenu: false,
    postgresConnections: null as any,
    userSearch: '',
    licenceSearch: '',
    clientSearch: '',
    logSearch: '',
    refreshTimer: null as any,
    period: '30d' as string,
    customDates: [] as string[],
    range: { start: '', end: '' },
    presets: [
      { text: 'Last 1 hour', value: '1h', ms: 3600e3 },
      { text: 'Last 6 hours', value: '6h', ms: 6 * 3600e3 },
      { text: 'Last 24 hours', value: '24h', ms: 24 * 3600e3 },
      { text: 'Last 7 days', value: '7d', ms: 7 * 864e5 },
      { text: 'Last 30 days', value: '30d', ms: 30 * 864e5 },
      { text: 'Last 90 days', value: '90d', ms: 90 * 864e5 }
    ] as Array<{ text: string; value: string; ms: number }>,
    logColorMap: {
      CRITICAL: '#991b1b',
      ERROR: '#dc2626',
      WARNING: '#d97706',
      INFO: '#2563eb',
      DEBUG: '#6b7280'
    } as Record<string, string>,
    recentUserHeaders: [
      { text: 'Name', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Registered', value: 'created_at' },
      { text: 'Status', value: 'is_active', sortable: false }
    ],
    expiringHeaders: [
      { text: 'User', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Licence', value: 'licence' },
      { text: 'Expiry', value: 'end_date' }
    ],
    recentClientHeaders: [
      { text: 'Client', value: 'name' },
      { text: 'Cordatus', value: 'cordatus_version' },
      { text: 'Location', value: 'location_type' },
      { text: 'API URL', value: 'api_url', sortable: false },
      { text: 'Last Health', value: 'last_health' },
      { text: 'Status', value: 'is_active', sortable: false }
    ],
    recentLogHeaders: [
      { text: 'Time', value: 'time' },
      { text: 'Level', value: 'level', sortable: false },
      { text: 'Message', value: 'message' },
      { text: 'Endpoint', value: 'endpoint' }
    ],
    services: [
      {
        name: 'Backend Service',
        status: 'Unknown',
        baseUrl: createUrl(
          environment.backendSecure,
          environment.backendUrl,
          environment.backendPort,
          'health-check-backend-service'
        ),
        loading: false
      },
      {
        name: 'Api Service',
        status: 'Unknown',
        baseUrl: createUrl(
          environment.apiSecure,
          environment.apiUrl,
          environment.apiPort,
          'health-check-api-service'
        ),
        loading: false
      },
      {
        name: 'Postgres Service',
        status: 'Unknown',
        baseUrl: createUrl(
          environment.backendSecure,
          environment.backendUrl,
          environment.backendPort,
          'health-check-postgres-service'
        ),
        activeConnection: true,
        loading: false
      },
      {
        name: 'Signal Service',
        status: 'Unknown',
        baseUrl: createUrl(
          environment.signalServerSecure,
          environment.signalServerUrl,
          environment.signalServerPort,
          'peerjs/healthCheck'
        ),
        loading: false
      },
      {
        name: 'Local Tunnel Service',
        status: 'Unknown',
        baseUrl: createUrl(
          environment.tunnelServerSecure,
          environment.tunnelServerUrl,
          environment.tunnelServerPort,
          'api/status'
        ),
        loading: false
      }
    ]
  }),
  computed: {
    ...mapGetters({
      summary: 'dashboardSummary',
      emailStats: 'dashboardEmailStats',
      loading: 'dashboardLoading',
      error: 'dashboardError'
    }),
    today (): string {
      return this.fmtDate(new Date())
    },
    rangeLabel (): string {
      const preset = this.presets.find((p: any) => p.value === this.period)
      if (preset) return preset.text
      if (this.range.start && this.range.end) {
        return `${this.range.start.slice(0, 10)} → ${this.range.end.slice(0, 10)}`
      }
      return 'Custom range'
    },
    offlineClients (): number {
      return Math.max(0, (this.summary.devices.total || 0) - (this.summary.devices.active || 0))
    },
    growthValues (): number[] {
      return this.summary.users_growth.map((g: any) => g.count)
    },
    growthLabels (): string[] {
      // Saatlik kova ("YYYY-MM-DD HH:00") -> "HH:00"; günlük -> "MM-DD".
      return this.summary.users_growth.map((g: any) => {
        const d = String(g.date)
        return d.length > 10 ? d.slice(11) : d.slice(5)
      })
    },
    roleBars (): any[] {
      return this.summary.users_by_role
        .slice(0, 3)
        .map((r: any) => ({
          label: r.label || r.role || '—',
          value: r.count
        }))
    },
    licenceTypeBars (): any[] {
      return this.summary.licences_by_type.map((l: any) => ({
        label: l.type,
        value: l.count
      }))
    },
    logLevelBars (): any[] {
      return this.summary.logs_by_level.map((l: any) => ({
        label: l.level,
        value: l.count
      }))
    },
    versionBars (): any[] {
      return this.summary.clients_by_version.map((c: any) => ({
        label: c.version,
        value: c.count
      }))
    },
    jetpackBars (): any[] {
      return this.summary.clients_by_jetpack.map((c: any) => ({
        label: c.version,
        value: c.count
      }))
    }
  },
  created () {
    const def = this.presets.find((p: any) => p.value === '24h') || this.presets[0]
    this.applyPreset(def)
    this.refreshTimer = window.setInterval(() => this.autoRefreshTick(), 60000)
  },
  beforeDestroy () {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  },
  methods: {
    ...mapActions(['getDashboardSummary']),
    async clearCache (): Promise<void> {
      this.clearingCache = true
      try {
        const res = await apiService.post('/admin/cache/clear')
        this.cacheSnack = { show: true, color: 'success', text: res.data.message }
      } catch (err: any) {
        const msg = (err.response && err.response.data && err.response.data.message) || err.message
        this.cacheSnack = { show: true, color: 'error', text: msg }
      } finally {
        this.clearingCache = false
      }
    },
    fmtDate (d: Date): string {
      const pad = (n: number) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    applyPreset (p: { value: string; ms: number }) {
      // Göreli aralık: [now - süre, now]. UTC ISO gönderilir; backend'deki
      // created_at/time sütunları (UTC) ile birebir eşleşsin diye.
      const end = new Date()
      const start = new Date(end.getTime() - p.ms)
      this.period = p.value
      this.range = { start: start.toISOString(), end: end.toISOString() }
      this.filterMenu = false
      this.fetchData()
    },
    applyCustom () {
      if (this.customDates.length < 2) return
      const sorted = [...this.customDates].sort()
      this.period = 'custom'
      // Takvim seçimi gün bazlı: başlangıç gün başı, bitiş gün sonu (UTC).
      this.range = {
        start: `${sorted[0]}T00:00:00.000Z`,
        end: `${sorted[1]}T23:59:59.999Z`
      }
      this.filterMenu = false
      this.fetchData()
    },
    fetchData () {
      this.getDashboardSummary({ start: this.range.start, end: this.range.end })
      this.initializeHealth()
    },
    refresh () {
      this.fetchData()
    },
    autoRefreshTick () {
      // Göreli aralıklarda (24h/7d vb.) pencereyi "şimdi"ye kaydır; özel
      // aralık seçiliyse sadece aynı aralığı yeniden çeker.
      const preset = this.presets.find((p: any) => p.value === this.period)
      if (preset) {
        this.applyPreset(preset)
      } else {
        this.fetchData()
      }
    },
    initializeHealth () {
      this.services.forEach((item: any) => this.healthCheck(item))
      this.fetchPostgresConnections()
    },
    fetchPostgresConnections () {
      apiService
        .get('active-database-connections')
        .then((response: any) => {
          this.postgresConnections = response.data.data
        })
        .catch(() => {
          this.postgresConnections = null
        })
    },
    async healthCheck (item: any) {
      item.loading = true
      await apiService
        .get(`${item.baseUrl}`)
        .then(() => {
          item.status = 'Running'
        })
        .catch(() => {
          item.status = 'Connection Lost'
        })
        .finally(() => {
          item.loading = false
        })
    },
    checkColor (status: string): string {
      if (status === 'Running') return 'green'
      if (status === 'Connection Lost') return 'red'
      return 'grey'
    }
  }
})
